import React, { useState, useEffect, useCallback } from "react";
import {
  getEventsByOwner, getEventWithdrawalsByOwner, createEventWithdrawal,
} from "../firebase/firestoreService";
import { MOBILE_OPERATORS } from "../services/paychangu";
import "../styles/myProfile.css";

const fmtMK = (n) => `MK ${Number(n || 0).toLocaleString()}`;
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const WITHDRAWAL_STATUS_STYLE = {
  Pending:  { background: "#fff3e0", color: "#f57c00" },
  Paid:     { background: "#e8f5e9", color: "#2e7d32" },
  Rejected: { background: "#fde8e8", color: "#c0392b" },
};

const eventRevenue = (event) =>
  (event.ticketTypes || []).reduce((s, t) => s + (t.sold || 0) * (t.price || 0), 0);

const eventSold = (event) =>
  (event.ticketTypes || []).reduce((s, t) => s + (t.sold || 0), 0);

export default function EventOwnerCard({ userId, ownerName }) {
  const [events,      setEvents]      = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [withdrawals, setWithdrawals] = useState([]);
  const [wLoading,    setWLoading]    = useState(true);
  const [formOpen,    setFormOpen]    = useState(false);
  const [wForm,       setWForm]       = useState({ operator: "airtel", phone: "", amount: "" });
  const [wSubmitting, setWSubmitting] = useState(false);
  const [wError,      setWError]      = useState("");

  useEffect(() => {
    getEventsByOwner(userId).then((e) => { setEvents(e); setEventsLoading(false); });
  }, [userId]);

  const loadWithdrawals = useCallback(() => {
    setWLoading(true);
    getEventWithdrawalsByOwner(userId).then((w) => { setWithdrawals(w); setWLoading(false); });
  }, [userId]);

  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);

  const setW = (k, v) => setWForm((f) => ({ ...f, [k]: v }));

  const totalRevenue = events.reduce((s, e) => s + eventRevenue(e), 0);
  const paidOut       = withdrawals.filter((w) => w.status === "Paid").reduce((s, w) => s + (w.amount || 0), 0);
  const pendingOut    = withdrawals.filter((w) => w.status === "Pending").reduce((s, w) => s + (w.amount || 0), 0);
  const available      = Math.max(0, totalRevenue - paidOut - pendingOut);

  const handleWithdrawRequest = async () => {
    const amount = parseFloat(wForm.amount);
    if (!wForm.phone.trim())          { setWError("Enter your mobile money number."); return; }
    if (isNaN(amount) || amount <= 0) { setWError("Enter a valid amount.");            return; }
    if (amount > available)           { setWError(`You can withdraw up to ${fmtMK(available)}.`); return; }

    setWSubmitting(true);
    setWError("");
    await createEventWithdrawal({
      ownerId: userId,
      ownerName: ownerName || "",
      operator: wForm.operator,
      phone:    wForm.phone.trim(),
      amount,
      status:   "Pending",
    });
    setWForm({ operator: "airtel", phone: "", amount: "" });
    setFormOpen(false);
    loadWithdrawals();
    setWSubmitting(false);
  };

  return (
    <div className="profile-card">
      <div className="profile-card__header">
        <h2 className="profile-card__title">My Events</h2>
        <span className="profile-card__count">{events.length} event{events.length !== 1 ? "s" : ""}</span>
      </div>

      {eventsLoading ? (
        <p className="profile-referral__commissions-empty">Loading…</p>
      ) : events.length === 0 ? (
        <p className="profile-referral__commissions-empty">No events assigned to you yet.</p>
      ) : (
        <div className="profile-referral__commission-list" style={{ marginBottom: 20 }}>
          {events.map((e) => (
            <div key={e.id} className="profile-referral__commission">
              <div className="profile-referral__commission-left">
                <p className="profile-referral__commission-date">{fmtDate(e.eventDate)}</p>
                <p className="profile-referral__commission-items">{e.name} · {eventSold(e)} sold</p>
              </div>
              <div className="profile-referral__commission-right">
                <p className="profile-referral__commission-earned">{fmtMK(eventRevenue(e))}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="profile-referral__balance-box">
        <div>
          <p className="profile-referral__link-label">Available to withdraw</p>
          <p className="profile-referral__stat-value" style={{ fontSize: 22 }}>{fmtMK(available)}</p>
        </div>
        <button
          className="profile-form__save"
          style={{ flex: "none", padding: "9px 16px" }}
          onClick={() => { setFormOpen((v) => !v); setWError(""); }}
          disabled={available <= 0 && !formOpen}
        >
          {formOpen ? "Cancel" : "Request Withdrawal"}
        </button>
      </div>

      {formOpen && (
        <div className="profile-form" style={{ marginTop: 12 }}>
          <div className="profile-form__group">
            <label className="profile-form__label">Operator</label>
            <select
              className="profile-form__input"
              value={wForm.operator}
              onChange={(e) => setW("operator", e.target.value)}
            >
              {MOBILE_OPERATORS.map((op) => (
                <option key={op.id} value={op.id}>{op.label}</option>
              ))}
            </select>
          </div>
          <div className="profile-form__group">
            <label className="profile-form__label">Mobile Money Number</label>
            <input
              className="profile-form__input"
              placeholder="e.g. 0990000000"
              value={wForm.phone}
              onChange={(e) => setW("phone", e.target.value)}
            />
          </div>
          <div className="profile-form__group">
            <label className="profile-form__label">Amount (MWK)</label>
            <input
              className="profile-form__input"
              type="number"
              min="1"
              placeholder={`Up to ${fmtMK(available)}`}
              value={wForm.amount}
              onChange={(e) => setW("amount", e.target.value)}
            />
          </div>
          {wError && <p className="profile-form__error">{wError}</p>}
          <div className="profile-form__actions">
            <button className="profile-form__save" onClick={handleWithdrawRequest} disabled={wSubmitting}>
              {wSubmitting ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </div>
      )}

      <div className="profile-referral__commissions">
        <p className="profile-referral__commissions-title">Withdrawal History</p>
        {wLoading ? (
          <p className="profile-referral__commissions-empty">Loading…</p>
        ) : withdrawals.length === 0 ? (
          <p className="profile-referral__commissions-empty">No withdrawal requests yet.</p>
        ) : (
          <div className="profile-referral__commission-list">
            {withdrawals.map((w) => (
              <div key={w.id} className="profile-referral__commission">
                <div className="profile-referral__commission-left">
                  <p className="profile-referral__commission-date">{fmtDate(w.createdAt)}</p>
                  <p className="profile-referral__commission-items">
                    {MOBILE_OPERATORS.find((o) => o.id === w.operator)?.label || w.operator} · {w.phone}
                  </p>
                </div>
                <div className="profile-referral__commission-right">
                  <p className="profile-referral__commission-total">{fmtMK(w.amount)}</p>
                  <span style={{
                    ...(WITHDRAWAL_STATUS_STYLE[w.status] || WITHDRAWAL_STATUS_STYLE.Pending),
                    fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "2px 8px", borderRadius: 20,
                  }}>
                    {w.status || "Pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="profile-referral__note">
        Payouts are sent manually to your chosen mobile money number, usually within 1–2 business days.
      </p>
    </div>
  );
}
